import { API_ORIGIN, apiUrl } from "./apiOrigin";
// src/DeliveryEfficiency.js
// =============================================================================
// Head office → Deliveries → Activity Review → Efficiency (2026-10)
//
// Are we sending one van when one would do? Judges each patient against their
// own rhythm (weekly dosette patients are fine; bunched trips are not):
//   • Summary tiles: visits, avoidable visits (and their cost), efficiency,
//     same-day repeats, redeliveries after a failed drop, steady patients.
//   • Monthly trend — necessary vs avoidable visits, efficiency per month.
//   • Days between deliveries — click a bar to list the patients with that gap.
//   • Patients, worst first — a timeline per patient, bunched trips ringed in
//     red; click a patient for every visit with round, driver, items and cause.
//   • All branches — efficiency side by side; click a branch to open it.
// The rules live in delivery_efficiency.js (shared with the backend).
// =============================================================================
import React, { useEffect, useMemo, useState } from "react";
import * as XLSX from "xlsx";
import { displayName as maskName } from "./nameMask";
import { analyseEfficiency, dayNo, HIST_MAX } from "./delivery_efficiency";
const API = apiUrl("/api/headoffice/delivery-review");
const C = {
  bunched: "#CF1322",
  redelivery: "#722ED1",
  cycle: "#389E0D",
  off: "#8C8C8C",
  ok: "#434343",
  failed: "#8C8C8C"
};
const CLASS_LABEL = {
  bunched: "Bunched (within the window) — avoidable",
  redelivery: "Redelivery after a failed drop",
  cycle: "On the patient’s usual cycle",
  off: "Other gap (off cycle)"
};
const PATTERN = {
  bunched: {
    label: "Bunched",
    bg: "#FFF1F0",
    fg: "#A8071A",
    tip: "Two or more avoidable trips, or one in five trips avoidable"
  },
  irregular: {
    label: "Irregular",
    bg: "#FFF7E6",
    fg: "#AD4E00",
    tip: "No avoidable trips, but the gaps between deliveries vary a lot"
  },
  steady: {
    label: "Steady",
    bg: "#F6FFED",
    fg: "#389E0D",
    tip: "Deliveries on a regular cycle with no bunching"
  },
  few: {
    label: "Too few to judge",
    bg: "#F5F5F5",
    fg: "#5D5D5D",
    tip: "Fewer than three deliveries in the period"
  }
};
const FLAG = {
  ok: {
    label: "Delivered",
    fg: "#434343"
  },
  avoidable: {
    label: "Avoidable",
    fg: "#A8071A"
  },
  sameday: {
    label: "Same-day repeat",
    fg: "#A8071A"
  },
  redelivery: {
    label: "Redelivery",
    fg: "#531DAB"
  },
  failed: {
    label: "Failed attempt",
    fg: "#5D5D5D"
  }
};
const num = n => (n || 0).toLocaleString("en-GB");
const pct = x => x == null ? "—" : `${Math.round(x * 100)}%`;
const gbp = x => `£${(x || 0).toLocaleString("en-GB", {
  minimumFractionDigits: 0,
  maximumFractionDigits: 0
})}`;
const ukDate = ymd => new Date(`${ymd}T12:00:00Z`).toLocaleDateString("en-GB", {
  weekday: "short",
  day: "numeric",
  month: "short",
  year: "2-digit"
});
const monthLabel = ym => new Date(+ym.slice(0, 4), +ym.slice(5, 7) - 1, 1).toLocaleString("en-GB", {
  month: "short",
  year: "2-digit"
});
const btn = on => ({
  padding: "5px 11px",
  borderRadius: 6,
  border: on ? "none" : "1px solid #D9D9D9",
  cursor: "pointer",
  background: on ? "#0E8CFF" : "#fff",
  color: on ? "#fff" : "#262626",
  fontSize: 12.5,
  fontWeight: on ? 600 : 400
});
const exportBtn = {
  padding: "5px 10px",
  borderRadius: 6,
  border: "1px solid #D9D9D9",
  background: "#fff",
  color: "#262626",
  cursor: "pointer",
  fontSize: 12,
  fontWeight: 600
};
const card = {
  border: "1px solid #F0F0F0",
  borderRadius: 10,
  padding: "12px 14px",
  background: "#fff",
  marginBottom: 12
};
const h3 = {
  margin: "0 0 2px",
  fontSize: 14,
  fontWeight: 700,
  color: "#262626"
};
const sub = {
  fontSize: 12,
  color: "#5D5D5D",
  marginBottom: 8
};
const th = {
  position: "sticky",
  top: 0,
  background: "#F5F5F5",
  borderBottom: "1px solid #D9D9D9",
  padding: "6px 8px",
  fontSize: 12,
  fontWeight: 600,
  textAlign: "right",
  cursor: "pointer",
  userSelect: "none",
  whiteSpace: "nowrap",
  zIndex: 1
};
const td = {
  borderBottom: "1px solid #EDF2F7",
  padding: "5px 8px",
  fontSize: 13,
  textAlign: "right",
  whiteSpace: "nowrap",
  verticalAlign: "middle"
};
function readLS(key, dflt) {
  try {
    const v = localStorage.getItem(key);
    return v == null ? dflt : JSON.parse(v);
  } catch {
    return dflt;
  }
}
function writeLS(key, v) {
  try {
    localStorage.setItem(key, JSON.stringify(v));
  } catch {/* ignore */}
}

// A hover tooltip positioned inside a relative container.
function useTip() {
  const [tip, setTip] = useState(null);
  const show = (e, content) => {
    const box = e.currentTarget.ownerSVGElement?.parentNode?.getBoundingClientRect() || e.currentTarget.parentNode.getBoundingClientRect();
    setTip({
      x: e.clientX - box.left,
      y: e.clientY - box.top,
      content
    });
  };
  const el = tip && <div style={{
    position: "absolute",
    left: Math.max(4, tip.x + 12),
    top: Math.max(4, tip.y - 10),
    pointerEvents: "none",
    background: "#262626",
    color: "#fff",
    fontSize: 12,
    padding: "6px 8px",
    borderRadius: 6,
    whiteSpace: "nowrap",
    zIndex: 5,
    boxShadow: "0 4px 12px rgba(0,0,0,.2)"
  }}>{tip.content}</div>;
  return {
    show,
    hide: () => setTip(null),
    el
  };
}

// ---------------------------------------------------------------------------
function Tiles({
  kpi,
  cost
}) {
  const tile = (label, value, note, accent) => <div style={{
    flex: "1 1 150px",
    minWidth: 150,
    border: "1px solid #F0F0F0",
    borderLeft: `4px solid ${accent || "#D9D9D9"}`,
    borderRadius: 8,
    padding: "10px 12px",
    background: "#fff"
  }}>
      <div style={{
      fontSize: 12,
      color: "#5D5D5D"
    }}>{label}</div>
      <div style={{
      fontSize: 24,
      fontWeight: 700,
      color: "#262626",
      lineHeight: 1.2
    }}>{value}</div>
      {note && <div style={{
      fontSize: 12,
      color: "#5D5D5D",
      marginTop: 2
    }}>{note}</div>}
    </div>;
  return <div style={{
    display: "flex",
    flexWrap: "wrap",
    gap: 10,
    marginBottom: 12
  }}>
      {tile("Visits made", num(kpi.delivered), `${num(kpi.patients)} patients · ${num(kpi.items)} items`)}
      {tile("Avoidable visits", num(kpi.avoidable), `${pct(kpi.avoidablePct)} of visits ≈ ${gbp(kpi.avoidable * cost)}`, C.bunched)}
      {tile("Efficiency", pct(kpi.efficiency), "necessary ÷ actual visits", kpi.efficiency == null ? null : kpi.efficiency >= 0.9 ? C.cycle : kpi.efficiency >= 0.8 ? "#d97706" : C.bunched)}
      {tile("Same-day repeats", num(kpi.sameDay), "second round, same patient, same day", C.bunched)}
      {tile("Redeliveries", num(kpi.redeliveries), `after ${num(kpi.failed)} failed attempts`, C.redelivery)}
      {tile("Patients on a steady cycle", pct(kpi.steadyPct), `${num(kpi.steady)} of ${num(kpi.judged)} with 3+ visits`, C.cycle)}
    </div>;
}

// ---------------------------------------------------------------------------
function MonthlyTrend({
  monthly
}) {
  const tip = useTip();
  if (!monthly.length) return null;
  const W = 640,
    H = 170,
    padL = 34,
    padB = 22,
    padT = 18;
  const max = Math.max(1, ...monthly.map(m => m.delivered));
  const bw = Math.min(48, (W - padL - 10) / monthly.length - 8);
  const step = (W - padL - 10) / monthly.length;
  const y = v => H - padB - v / max * (H - padB - padT);
  const ticks = [0, Math.round(max / 2), max];
  return <div style={card}>
      <div style={h3}>Month by month</div>
      <div style={sub}>Delivered visits each month: <b style={{
        color: C.ok
      }}>necessary</b> and <b style={{
        color: C.bunched
      }}>avoidable</b>; the figure above each bar is that month’s efficiency.</div>
      <div style={{
      position: "relative"
    }}>
        <svg viewBox={`0 0 ${W} ${H}`} style={{
        width: "100%",
        maxWidth: W,
        height: "auto",
        display: "block"
      }} role="img" aria-label="Visits per month, necessary and avoidable">
          {ticks.map(t => <g key={t}><line x1={padL} x2={W - 6} y1={y(t)} y2={y(t)} stroke="#EDF2F7" /><text x={padL - 6} y={y(t) + 4} fontSize="10" fill="#5D5D5D" textAnchor="end">{t}</text></g>)}
          {monthly.map((m, i) => {
          const x = padL + i * step + (step - bw) / 2;
          const nec = m.delivered - m.avoidable;
          const eff = m.delivered ? nec / m.delivered : null;
          return <g key={m.month} onMouseMove={e => tip.show(e, <div><b>{monthLabel(m.month)}</b><br />{num(m.delivered)} visits · {num(m.avoidable)} avoidable ({num(m.sameDay)} same-day)<br />{num(m.redeliveries)} redeliveries · {num(m.failed)} failed attempts<br />Efficiency {pct(eff)}</div>)} onMouseLeave={tip.hide}>
                <rect x={x - 4} y={padT - 14} width={bw + 8} height={H - padT} fill="transparent" />
                {nec > 0 && <rect x={x} y={y(nec)} width={bw} height={Math.max(0, H - padB - y(nec))} fill={C.ok} rx="3" />}
                {m.avoidable > 0 && <rect x={x} y={y(m.delivered)} width={bw} height={Math.max(1, y(nec) - y(m.delivered) - 2)} fill={C.bunched} rx="3" />}
                <text x={x + bw / 2} y={y(m.delivered) - 4} fontSize="10.5" fontWeight="600" fill="#262626" textAnchor="middle">{pct(eff)}</text>
                <text x={x + bw / 2} y={H - 6} fontSize="10.5" fill="#5D5D5D" textAnchor="middle">{monthLabel(m.month)}</text>
              </g>;
        })}
        </svg>
        {tip.el}
      </div>
    </div>;
}

// ---------------------------------------------------------------------------
function GapHistogram({
  hist,
  windowDays,
  selected,
  onSelect
}) {
  const tip = useTip();
  const W = 680,
    H = 190,
    padL = 34,
    padB = 30,
    padT = 10;
  const total = h => h.bunched + h.redelivery + h.cycle + h.off;
  const max = Math.max(1, ...hist.map(total));
  const step = (W - padL - 8) / hist.length;
  const bw = Math.max(4, step - 2);
  const y = v => H - padB - v / max * (H - padB - padT);
  const order = ["cycle", "off", "redelivery", "bunched"];
  return <div style={card}>
      <div style={{
      display: "flex",
      alignItems: "baseline",
      gap: 10,
      flexWrap: "wrap"
    }}>
        <div style={h3}>Days between deliveries to the same patient</div>
        {selected != null && <button style={btn(false)} onClick={() => onSelect(null)}>Clear: {selected >= HIST_MAX ? `${HIST_MAX}+` : selected} day gap ✕</button>}
      </div>
      <div style={sub}>Every gap between one delivered visit and the next. Tall red bars on the left are the problem. <b>Click a bar</b> to list the patients with that gap.</div>
      <div style={{
      position: "relative"
    }}>
        <svg viewBox={`0 0 ${W} ${H}`} style={{
        width: "100%",
        maxWidth: W,
        height: "auto",
        display: "block"
      }} role="img" aria-label="Histogram of days between deliveries">
          <rect x={padL} y={padT} width={(windowDays + 1) * step} height={H - padB - padT} fill="#FFF1F0" />
          <text x={padL + 4} y={padT + 11} fontSize="10" fill="#A8071A">0–{windowDays} days: avoidable</text>
          {[0, Math.round(max / 2), max].map(t => <g key={t}><line x1={padL} x2={W - 6} y1={y(t)} y2={y(t)} stroke="#EDF2F7" /><text x={padL - 6} y={y(t) + 4} fontSize="10" fill="#5D5D5D" textAnchor="end">{t}</text></g>)}
          {hist.map((h, i) => {
          const x = padL + i * step + 1;
          let acc = 0;
          const tot = total(h);
          const dim = selected != null && selected !== h.gap;
          return <g key={h.gap} style={{
            cursor: tot ? "pointer" : "default"
          }} opacity={dim ? 0.35 : 1} onClick={() => tot && onSelect(selected === h.gap ? null : h.gap)} onMouseMove={e => tip.show(e, <div><b>{h.gap >= HIST_MAX ? `${HIST_MAX}+ days` : `${h.gap} day${h.gap === 1 ? "" : "s"}`}</b> — {num(tot)} gaps
                  {order.filter(k => h[k]).map(k => <div key={k}><span style={{
                display: "inline-block",
                width: 8,
                height: 8,
                background: C[k],
                borderRadius: 2,
                marginRight: 5
              }} />{CLASS_LABEL[k]}: {num(h[k])}</div>)}
                  {tot ? <div style={{
              opacity: 0.8,
              marginTop: 2
            }}>Click to list these patients</div> : null}</div>)} onMouseLeave={tip.hide}>
                <rect x={x - 1} y={padT} width={step} height={H - padB - padT} fill="transparent" />
                {order.map(k => {
              if (!h[k]) return null;
              const y0 = y(acc),
                y1 = y(acc + h[k]);
              acc += h[k];
              return <rect key={k} x={x} y={y1} width={bw} height={Math.max(1, y0 - y1 - (acc < tot ? 1 : 0))} fill={C[k]} rx="1.5" />;
            })}
                {(h.gap % 7 === 0 || h.gap === HIST_MAX) && <text x={x + bw / 2} y={H - padB + 13} fontSize="10" fill="#5D5D5D" textAnchor="middle">{h.gap >= HIST_MAX ? `${HIST_MAX}+` : h.gap}</text>}
              </g>;
        })}
          <text x={padL + (W - padL) / 2} y={H - 3} fontSize="10.5" fill="#5D5D5D" textAnchor="middle">days since the previous delivery</text>
        </svg>
        {tip.el}
      </div>
      <div style={{
      display: "flex",
      flexWrap: "wrap",
      gap: 14,
      fontSize: 12,
      color: "#434343",
      marginTop: 4
    }}>
        {["bunched", "redelivery", "cycle", "off"].map(k => <span key={k}><span style={{
          display: "inline-block",
          width: 10,
          height: 10,
          background: C[k],
          borderRadius: 2,
          marginRight: 5,
          verticalAlign: -1
        }} />{CLASS_LABEL[k]}</span>)}
      </div>
    </div>;
}

// ---------------------------------------------------------------------------
// One patient's visits across the period as dots; bunched groups ringed.
function Timeline({
  visits,
  d0,
  d1,
  width = 260
}) {
  const H = 26,
    pad = 6;
  const span = Math.max(1, d1 - d0);
  const x = n => pad + (n - d0) / span * (width - 2 * pad);
  const groups = [];
  let g = null;
  for (const v of visits) {
    if (!v.s) continue;
    if (v.flag === "ok" || v.flag === "redelivery" || !g) {
      g = [v];
      groups.push(g);
    } else g.push(v);
  }
  return <svg width={width} height={H} style={{
    display: "block"
  }} aria-hidden="true">
      <line x1={pad} x2={width - pad} y1={H / 2} y2={H / 2} stroke="#F0F0F0" strokeWidth="2" />
      {groups.filter(gr => gr.some(v => v.flag === "avoidable" || v.flag === "sameday")).map((gr, i) => {
      const a = x(gr[0].n),
        b = x(gr[gr.length - 1].n);
      return <rect key={i} x={a - 7} y={H / 2 - 8} width={b - a + 14} height={16} rx="8" fill="#FFF1F0" stroke={C.bunched} strokeWidth="1.5" />;
    })}
      {visits.map((v, i) => {
      const cx = x(v.n);
      const tt = `${ukDate(v.d)} ${v.t || ""} — ${FLAG[v.flag]?.label || ""}${v.gap != null ? ` (${v.gap} d after previous)` : ""}`;
      if (!v.s) return <g key={i}><title>{tt}</title><circle cx={cx} cy={H / 2} r="4" fill="#fff" stroke={C.failed} strokeWidth="1.8" /></g>;
      const fill = v.flag === "avoidable" || v.flag === "sameday" ? C.bunched : v.flag === "redelivery" ? C.redelivery : C.ok;
      return <g key={i}><title>{tt}</title><circle cx={cx} cy={H / 2} r="4.5" fill={fill} stroke="#fff" strokeWidth="1.5" /></g>;
    })}
    </svg>;
}
function PatientDetail({
  p,
  driverName
}) {
  return <div style={{
    padding: "8px 10px 12px 28px",
    background: "#FAFAFA"
  }}>
      <table style={{
      borderCollapse: "collapse",
      fontSize: 12.5,
      minWidth: 720
    }}>
        <thead><tr>{["Date", "Time", "Round", "Driver", "Items", "Outcome", "Days since previous", "Why it’s flagged"].map(h => <th key={h} style={{
            ...th,
            position: "static",
            cursor: "default",
            textAlign: h === "Why it’s flagged" || h === "Driver" ? "left" : "right"
          }}>{h}</th>)}</tr></thead>
        <tbody>
          {p.visits.map((v, i) => <tr key={i} style={{
          background: v.flag === "avoidable" || v.flag === "sameday" ? "#FFF1F0" : v.flag === "redelivery" ? "#F9F0FF" : undefined
        }}>
              <td style={td}>{ukDate(v.d)}</td>
              <td style={td}>{v.t || ""}</td>
              <td style={{
            ...td,
            fontFamily: "monospace",
            color: "#5D5D5D"
          }} title={v.r}>{v.r ? `…${v.r.slice(-6)}` : "(none)"}</td>
              <td style={{
            ...td,
            textAlign: "left"
          }}>{driverName(v.v)}</td>
              <td style={td}>{v.i}</td>
              <td style={{
            ...td,
            color: FLAG[v.flag]?.fg,
            fontWeight: v.flag === "ok" ? 400 : 600
          }}>{FLAG[v.flag]?.label}</td>
              <td style={td}>{v.gap ?? ""}</td>
              <td style={{
            ...td,
            textAlign: "left",
            whiteSpace: "normal",
            maxWidth: 380,
            color: "#434343"
          }}>{v.cause}</td>
            </tr>)}
        </tbody>
      </table>
    </div>;
}
function PatientList({
  patients,
  info,
  d0,
  d1,
  masked,
  driverName,
  gapFilter,
  onExport
}) {
  const [pattern, setPattern] = useState("all");
  const [sort, setSort] = useState({
    key: "avoidable",
    dir: "desc"
  });
  const [open, setOpen] = useState(null);
  const [limit, setLimit] = useState(50);
  useEffect(() => {
    setLimit(50);
  }, [pattern, gapFilter, patients]);
  const counts = useMemo(() => patients.reduce((c, p) => {
    c[p.pattern] = (c[p.pattern] || 0) + 1;
    return c;
  }, {}), [patients]);
  const filtered = patients.filter(p => (pattern === "all" || p.pattern === pattern) && (gapFilter == null || p.visits.some(v => v.gap != null && Math.min(v.gap, HIST_MAX) === gapFilter)));
  const get = (p, k) => k === "name" ? info(p.patientId).name : k === "postcode" ? info(p.patientId).postcode : k === "cycle" ? p.cycle ?? 9999 : k === "cv" ? p.cv ?? -1 : p[k];
  const rows = [...filtered].sort((a, b) => {
    const va = get(a, sort.key),
      vb = get(b, sort.key);
    const c = typeof va === "string" ? String(va).localeCompare(String(vb)) : (va || 0) - (vb || 0);
    return sort.dir === "asc" ? c : -c;
  });
  const toggle = key => setSort(s => s.key === key ? {
    key,
    dir: s.dir === "asc" ? "desc" : "asc"
  } : {
    key,
    dir: ["name", "postcode", "cycle"].includes(key) ? "asc" : "desc"
  });
  const arrow = key => sort.key === key ? sort.dir === "asc" ? " ▲" : " ▼" : "";
  const col = (key, label, title, left) => <th style={{
    ...th,
    textAlign: left ? "left" : "right"
  }} onClick={() => toggle(key)} title={title}>{label}{arrow(key)}</th>;
  return <div style={card}>
      <div style={{
      display: "flex",
      alignItems: "baseline",
      gap: 10,
      flexWrap: "wrap"
    }}>
        <div style={h3}>Patients, worst first</div>
        <span style={{
        flex: 1
      }} />
        <button style={exportBtn} onClick={() => onExport(rows)}>Export to Excel</button>
      </div>
      <div style={sub}>
        One dot per visit: <b style={{
        color: C.ok
      }}>●</b> delivered, <b style={{
        color: C.bunched
      }}>●</b> avoidable (ringed with the trip it could have joined), <b style={{
        color: C.redelivery
      }}>●</b> redelivery after a failed drop, <b style={{
        color: C.failed
      }}>○</b> failed attempt. Hover a dot for its date; click a patient for every visit.
      </div>
      <div style={{
      display: "flex",
      gap: 6,
      flexWrap: "wrap",
      marginBottom: 8,
      alignItems: "center"
    }}>
        <button style={btn(pattern === "all")} onClick={() => setPattern("all")}>All ({num(patients.length)})</button>
        {Object.entries(PATTERN).map(([k, p]) => <button key={k} style={btn(pattern === k)} title={p.tip} onClick={() => setPattern(k)}>{p.label} ({num(counts[k] || 0)})</button>)}
        {gapFilter != null && <span style={{
        fontSize: 12,
        color: "#A8071A"
      }}>Showing patients with a {gapFilter >= HIST_MAX ? `${HIST_MAX}+` : gapFilter}-day gap</span>}
      </div>
      <div style={{
      maxHeight: 620,
      overflow: "auto",
      border: "1px solid #F0F0F0",
      borderRadius: 8
    }}>
        <table style={{
        borderCollapse: "collapse",
        width: "100%"
      }}>
          <thead><tr>
            {col("name", "Patient", "", true)}{col("postcode", "Postcode", "", true)}
            <th style={{
              ...th,
              textAlign: "left",
              cursor: "default"
            }}>Visits across the period</th>
            {col("delivered", "Visits", "Delivered visits")}{col("avoidable", "Avoidable", "Visits within the window of the trip before")}
            {col("sameDay", "Same day", "Second round on the same day")}{col("redeliveries", "Redeliveries", "Delivered after a failed drop")}
            {col("failed", "Failed", "Failed attempts")}{col("cycle", "Usual cycle", "Median gap between deliveries, ignoring bunched ones")}
            {col("cv", "Irregularity", "How much the gaps vary (0% = like clockwork)")}
            <th style={{
              ...th,
              textAlign: "left",
              cursor: "default"
            }}>Pattern</th>
          </tr></thead>
          <tbody>
            {rows.slice(0, limit).map(p => {
            const inf = info(p.patientId);
            const pat = PATTERN[p.pattern];
            const isOpen = open === p.patientId;
            return <React.Fragment key={p.patientId}>
                  <tr style={{
                cursor: "pointer",
                background: isOpen ? "#E6F7FF" : undefined
              }} onClick={() => setOpen(isOpen ? null : p.patientId)}>
                    <td style={{
                  ...td,
                  textAlign: "left",
                  fontWeight: 600
                }}>{isOpen ? "▾ " : "▸ "}{maskName(inf.name, masked)}</td>
                    <td style={{
                  ...td,
                  textAlign: "left"
                }}>{inf.postcode}</td>
                    <td style={{
                  ...td,
                  textAlign: "left",
                  padding: "2px 8px"
                }}><Timeline visits={p.visits} d0={d0} d1={d1} /></td>
                    <td style={td}>{p.delivered}</td>
                    <td style={{
                  ...td,
                  color: p.avoidable ? "#A8071A" : undefined,
                  fontWeight: p.avoidable ? 700 : 400
                }}>{p.avoidable}</td>
                    <td style={td}>{p.sameDay || ""}</td>
                    <td style={td}>{p.redeliveries || ""}</td>
                    <td style={td}>{p.failed || ""}</td>
                    <td style={td}>{p.cycleLabel || "—"}</td>
                    <td style={td}>{p.cv == null ? "—" : `${Math.round(p.cv * 100)}%`}</td>
                    <td style={{
                  ...td,
                  textAlign: "left"
                }}><span title={pat.tip} style={{
                    background: pat.bg,
                    color: pat.fg,
                    padding: "2px 8px",
                    borderRadius: 10,
                    fontSize: 12,
                    fontWeight: 600
                  }}>{pat.label}</span></td>
                  </tr>
                  {isOpen && <tr><td colSpan={11} style={{
                  padding: 0
                }}><PatientDetail p={p} driverName={driverName} /></td></tr>}
                </React.Fragment>;
          })}
            {!rows.length && <tr><td colSpan={11} style={{
              ...td,
              textAlign: "center",
              color: "#5D5D5D",
              padding: 16
            }}>No patients match.</td></tr>}
          </tbody>
        </table>
      </div>
      {rows.length > limit && <div style={{
      marginTop: 8
    }}><button style={btn(false)} onClick={() => setLimit(l => l + 100)}>Show more ({num(rows.length - limit)} not shown)</button></div>}
    </div>;
}

// ---------------------------------------------------------------------------
function BranchComparison({
  from,
  to,
  windowDays,
  cost,
  masked,
  onPick
}) {
  const [state, setState] = useState({
    busy: true,
    error: "",
    rows: []
  });
  const [sortBy, setSortBy] = useState("efficiency");
  const tip = useTip();
  useEffect(() => {
    let off = false;
    setState(s => ({
      ...s,
      busy: true,
      error: ""
    }));
    (async () => {
      try {
        const r = await fetch(`${API}/efficiency-summary`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("authToken")}`
          },
          body: JSON.stringify({
            from,
            to,
            windowDays
          })
        });
        const d = await r.json();
        if (!r.ok) throw new Error(d.error || `HTTP ${r.status}`);
        if (!off) setState({
          busy: false,
          error: "",
          rows: d.branches || []
        });
      } catch (e) {
        if (!off) setState({
          busy: false,
          error: e.message,
          rows: []
        });
      }
    })();
    return () => {
      off = true;
    };
  }, [from, to, windowDays]);
  const rows = state.rows.filter(r => !r.error && r.delivered > 0).sort((a, b) => sortBy === "efficiency" ? (a.efficiency ?? 1) - (b.efficiency ?? 1) : b.avoidable - a.avoidable);
  const missing = state.rows.filter(r => r.error || !r.delivered);
  const tot = rows.reduce((t, r) => ({
    delivered: t.delivered + r.delivered,
    avoidable: t.avoidable + r.avoidable
  }), {
    delivered: 0,
    avoidable: 0
  });
  const exportIt = () => {
    const ws = XLSX.utils.json_to_sheet(rows.map(r => ({
      Branch: maskName(r.pharmacyName, masked),
      "Visits": r.delivered,
      "Avoidable visits": r.avoidable,
      "Avoidable %": r.avoidablePct == null ? "" : Math.round(r.avoidablePct * 1000) / 10,
      "Efficiency %": r.efficiency == null ? "" : Math.round(r.efficiency * 1000) / 10,
      "Same-day repeats": r.sameDay,
      "Redeliveries": r.redeliveries,
      "Failed attempts": r.failed,
      "Steady patients %": r.steadyPct == null ? "" : Math.round(r.steadyPct * 100),
      "Est. cost of avoidable visits (£)": Math.round(r.avoidable * cost * 100) / 100
    })));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Branches");
    XLSX.writeFile(wb, `delivery_efficiency_branches_${from}_to_${to}.xlsx`);
  };
  return <div style={card}>
      <div style={{
      display: "flex",
      alignItems: "baseline",
      gap: 10,
      flexWrap: "wrap"
    }}>
        <div style={h3}>All branches, {windowDays}-day window</div>
        <span style={{
        fontSize: 12,
        color: "#5D5D5D"
      }}>Sort:</span>
        <button style={btn(sortBy === "efficiency")} onClick={() => setSortBy("efficiency")}>Least efficient</button>
        <button style={btn(sortBy === "avoidable")} onClick={() => setSortBy("avoidable")}>Most avoidable visits</button>
        <span style={{
        flex: 1
      }} />
        {rows.length > 0 && <button style={exportBtn} onClick={exportIt}>Export to Excel</button>}
      </div>
      <div style={sub}>Each bar is a branch’s efficiency; the red part is the share of trips that were avoidable. <b>Click a branch</b> to open it.
        {rows.length > 0 && <> Across these branches: {num(tot.avoidable)} avoidable of {num(tot.delivered)} visits ≈ <b>{gbp(tot.avoidable * cost)}</b>.</>}</div>
      {state.busy && <div style={{
      fontSize: 13,
      color: "#5D5D5D"
    }}>Working through every branch…</div>}
      {state.error && <div role="alert" style={{
      fontSize: 13,
      color: "#A8071A"
    }}>{state.error}</div>}
      {!state.busy && !state.error && <div style={{
      position: "relative"
    }}>
          {rows.map(r => {
        const eff = r.efficiency ?? 1;
        return <div key={r.dbName} onClick={() => onPick(r)} style={{
          display: "grid",
          gridTemplateColumns: "minmax(120px, 220px) 1fr 150px",
          alignItems: "center",
          gap: 10,
          padding: "4px 2px",
          cursor: "pointer",
          borderBottom: "1px solid #F5F5F5"
        }} onMouseMove={e => tip.show(e, <div><b>{maskName(r.pharmacyName, masked)}</b><br />{num(r.delivered)} visits · {num(r.avoidable)} avoidable ({num(r.sameDay)} same-day)<br />{num(r.redeliveries)} redeliveries · steady patients {pct(r.steadyPct)}<br />Click to open</div>)} onMouseLeave={tip.hide}>
                <div style={{
            fontSize: 13,
            fontWeight: 600,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap"
          }}>{maskName(r.pharmacyName, masked)}</div>
                <div style={{
            display: "flex",
            height: 16,
            borderRadius: 4,
            overflow: "hidden",
            background: "#F5F5F5"
          }}>
                  <div style={{
              width: `${eff * 100}%`,
              background: C.ok
            }} />
                  <div style={{
              width: `${(1 - eff) * 100}%`,
              background: C.bunched,
              borderLeft: "2px solid #fff"
            }} />
                </div>
                <div style={{
            fontSize: 12.5,
            color: "#434343",
            whiteSpace: "nowrap"
          }}><b>{pct(r.efficiency)}</b> · {num(r.avoidable)} avoidable · {gbp(r.avoidable * cost)}</div>
              </div>;
      })}
          {!rows.length && <div style={{
        fontSize: 13,
        color: "#5D5D5D"
      }}>No deliveries in these dates.</div>}
          {missing.length > 0 && <div style={{
        fontSize: 12,
        color: "#5D5D5D",
        marginTop: 6
      }}>No deliveries recorded: {missing.map(m => maskName(m.pharmacyName, masked) + (m.error ? " (could not be read)" : "")).join(", ")}</div>}
          {tip.el}
        </div>}
    </div>;
}

// ---------------------------------------------------------------------------
export default function DeliveryEfficiency({
  data,
  from,
  to,
  masked,
  match,
  search,
  onPickPharmacy
}) {
  const [windowDays, setWindowDays] = useState(() => readLS("deliveryEffWindow", 5));
  const [cost, setCost] = useState(() => readLS("deliveryEffCost", 2.5));
  const [view, setView] = useState(data ? "branch" : "all");
  const [gapFilter, setGapFilter] = useState(null);
  useEffect(() => writeLS("deliveryEffWindow", windowDays), [windowDays]);
  useEffect(() => writeLS("deliveryEffCost", cost), [cost]);
  useEffect(() => {
    if (!data) setView("all");
  }, [data]);
  useEffect(() => {
    setGapFilter(null);
  }, [data, windowDays, search]);
  const people = useMemo(() => {
    const m = new Map();
    if (data) for (const r of [...data.patients.success, ...data.patients.failed]) if (!m.has(r.patientId)) m.set(r.patientId, {
      name: r.name,
      postcode: r.postcode
    });
    return m;
  }, [data]);
  const drivers = useMemo(() => {
    const m = new Map();
    if (data) for (const r of [...data.drivers.success, ...data.drivers.failed]) m.set(r.driverId, r.name);
    return m;
  }, [data]);
  const info = pid => people.get(pid) || {
    name: "(patient not found)",
    postcode: ""
  };
  const driverName = id => id ? maskName(drivers.get(id) || "(driver not found)", masked) : "(no driver recorded)";

  // Search narrows the patients analysed, so tiles and charts follow it.
  const visits = useMemo(() => {
    if (!data?.visits) return null;
    if (!search) return data.visits;
    return Object.fromEntries(Object.entries(data.visits).filter(([pid]) => match({
      ...info(pid)
    })));
  }, [data, search]); // eslint-disable-line react-hooks/exhaustive-deps
  const result = useMemo(() => visits ? analyseEfficiency(visits, {
    windowDays
  }) : null, [visits, windowDays]);
  const d0 = dayNo(from),
    d1 = dayNo(to);
  const exportAll = patientRows => {
    if (!result) return;
    const wb = XLSX.utils.book_new();
    const k = result.kpi;
    const meta = [["Delivery efficiency"], [`Pharmacy: ${maskName(data.pharmacyName, masked)}`], [`Dates: ${from} to ${to}`], [`Avoidable = a delivered visit within ${windowDays} days of the trip before it (redeliveries after a failed drop are not counted as avoidable)`], ...(search ? [[`Search: "${search}"`]] : []), [`Exported: ${new Date().toLocaleString("en-GB")}`], []];
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([...meta, ["Visits made", k.delivered], ["Necessary visits", k.necessary], ["Avoidable visits", k.avoidable], ["Avoidable %", k.avoidablePct == null ? "" : Math.round(k.avoidablePct * 1000) / 10], ["Efficiency %", k.efficiency == null ? "" : Math.round(k.efficiency * 1000) / 10], ["Same-day repeats", k.sameDay], ["Redeliveries after a failed drop", k.redeliveries], ["Failed attempts", k.failed], ["Patients", k.patients], ["Patients on a steady cycle (of those with 3+ visits)", `${k.steady} of ${k.judged}`], ["Cost per drop (£)", cost], ["Est. cost of avoidable visits (£)", Math.round(k.avoidable * cost * 100) / 100]]), "Summary");
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(result.monthly.map(m => ({
      Month: monthLabel(m.month),
      Visits: m.delivered,
      Avoidable: m.avoidable,
      "Same-day": m.sameDay,
      Redeliveries: m.redeliveries,
      "Failed attempts": m.failed,
      "Efficiency %": m.delivered ? Math.round((1 - m.avoidable / m.delivered) * 1000) / 10 : ""
    }))), "Monthly");
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(result.hist.map(h => ({
      "Days between deliveries": h.gap >= HIST_MAX ? `${HIST_MAX}+` : h.gap,
      "Bunched (avoidable)": h.bunched,
      "Redelivery": h.redelivery,
      "On cycle": h.cycle,
      "Other": h.off
    }))), "Gaps");
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet((patientRows || result.patients).map(p => ({
      Patient: maskName(info(p.patientId).name, masked),
      Postcode: info(p.patientId).postcode,
      Visits: p.delivered,
      Avoidable: p.avoidable,
      "Same day": p.sameDay,
      Redeliveries: p.redeliveries,
      Failed: p.failed,
      Items: p.items,
      "Usual cycle": p.cycleLabel,
      "Irregularity %": p.cv == null ? "" : Math.round(p.cv * 100),
      Pattern: PATTERN[p.pattern].label
    }))), "Patients");
    const flagged = [];
    for (const p of result.patients) for (const v of p.visits) if (v.flag !== "ok") flagged.push({
      Patient: maskName(info(p.patientId).name, masked),
      Postcode: info(p.patientId).postcode,
      Date: v.d,
      Time: v.t,
      Round: v.r,
      Driver: driverName(v.v),
      Items: v.i,
      Outcome: FLAG[v.flag].label,
      "Days since previous": v.gap ?? "",
      Why: v.cause
    });
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(flagged), "Flagged visits");
    XLSX.writeFile(wb, `delivery_efficiency_${(data.pharmacyName || "pharmacy").replace(/[^a-z0-9]+/gi, "_")}_${from}_to_${to}.xlsx`);
  };
  return <div>
      <div style={{
      display: "flex",
      flexWrap: "wrap",
      gap: 12,
      alignItems: "center",
      marginBottom: 10,
      fontSize: 13
    }}>
        <span style={{
        display: "inline-flex",
        gap: 4
      }}>
          <button style={btn(view === "branch")} disabled={!data} onClick={() => setView("branch")} title={data ? "" : "Choose a pharmacy first"}>This pharmacy</button>
          <button style={btn(view === "all")} onClick={() => setView("all")}>All branches</button>
        </span>
        <label title="A delivered visit this many days or fewer after the trip before it counts as avoidable">Avoidable if within{" "}
          <input type="number" min="0" max="30" value={windowDays} onChange={e => setWindowDays(Math.max(0, Math.min(30, Math.round(+e.target.value || 0))))} style={{
          width: 50,
          padding: "3px 5px"
        }} /> days of the trip before</label>
        <label>Cost per drop £<input type="number" min="0" step="0.1" value={cost} onChange={e => setCost(Math.max(0, +e.target.value || 0))} style={{
          width: 64,
          padding: "3px 5px"
        }} /></label>
        <span style={{
        flex: 1
      }} />
        {view === "branch" && result && <button style={exportBtn} onClick={() => exportAll()}>Export efficiency to Excel</button>}
      </div>

      {view === "all" && <BranchComparison from={from} to={to} windowDays={windowDays} cost={cost} masked={masked} onPick={r => {
      onPickPharmacy(r);
      setView("branch");
    }} />}

      {view === "branch" && data && !data.visits && <div style={{
      fontSize: 13,
      color: "#AD4E00"
    }}>The server hasn’t been updated with the efficiency data yet — restart the backend after copying the new files.</div>}
      {view === "branch" && result && <>
        {search && <div style={{
        fontSize: 12,
        color: "#5D5D5D",
        marginBottom: 6
      }}>Only patients matching “{search}”.</div>}
        <Tiles kpi={result.kpi} cost={cost} />
        <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(420px, 1fr))",
        gap: 12
      }}>
          <GapHistogram hist={result.hist} windowDays={result.kpi.windowDays} selected={gapFilter} onSelect={setGapFilter} />
          <MonthlyTrend monthly={result.monthly} />
        </div>
        <PatientList patients={result.patients} info={info} d0={d0} d1={d1} masked={masked} driverName={driverName} gapFilter={gapFilter} onExport={exportAll} />
      </>}
    </div>;
}
